import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaCheck } from 'react-icons/fa';
import fnc from '../mymod/commFunction';
import './FindModals.scss';

const VERIFY_SECONDS = 5 * 60;

const FindPasswordModal = ({ onClose }) => {
  const [userId, setUserId] = useState('');
  const [hp, setHp] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      clearInterval(timerRef.current);
    };
  }, [onClose]);

  function startTimer() {
    clearInterval(timerRef.current);
    setTimeLeft(VERIFY_SECONDS);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  function formatTime(sec) {
    return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
  }

  function handleSend(e) {
    e.preventDefault();
    if (!userId.trim()) { alert('아이디를 입력해주세요.'); return; }
    if (!hp.trim()) { alert('휴대폰 번호를 입력해주세요.'); return; }
    setSending(true);
    fnc.sendAxios('/findIdRoutes/sendResetCode', { id: userId.trim(), hp: hp.trim() }, (res) => {
      setSending(false);
      if (res.error) { alert(res.message || '인증번호 전송 실패'); return; }
      setCodeSent(true);
      setCode(''); setNewPw(''); setConfirmPw('');
      startTimer();
    });
  }

  function handleReset(e) {
    e.preventDefault();
    if (!code.trim()) { alert('인증번호를 입력해주세요.'); return; }
    if (timeLeft === 0) { alert('인증번호가 만료되었습니다. 다시 요청해주세요.'); return; }
    if (!newPw) { alert('새 비밀번호를 입력해주세요.'); return; }
    if (newPw.length < 6) { alert('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (newPw !== confirmPw) { alert('비밀번호가 일치하지 않습니다.'); return; }
    fnc.sendAxios('/findIdRoutes/resetPassword', {
      id: userId.trim(), hp: hp.trim(), code: code.trim(), newPw,
    }, (res) => {
      if (res.error) { alert(res.message || '비밀번호 변경 실패'); return; }
      clearInterval(timerRef.current);
      setResetDone(true);
    });
  }

  const step = resetDone ? 3 : codeSent ? 2 : 1;

  return createPortal(
    <div className='find-modal-overlay'>
      <div className='find-modal'>
        <div className='modal-header'>
          <h3>비밀번호 찾기</h3>
          <button className='btn-close' type='button' onClick={onClose}>✕</button>
        </div>

        <div className='modal-body'>
          {/* 단계 표시 */}
          <div className='step-indicator'>
            <div className={`step-dot ${step >= 1 ? 'done' : ''} ${step === 1 ? 'active' : ''}`}>
              <span>1</span><em>정보 입력</em>
            </div>
            <div className={`step-line ${step >= 2 ? 'done' : ''}`} />
            <div className={`step-dot ${step >= 2 ? 'done' : ''} ${step === 2 ? 'active' : ''}`}>
              <span>2</span><em>인증+변경</em>
            </div>
            <div className={`step-line ${step >= 3 ? 'done' : ''}`} />
            <div className={`step-dot ${step >= 3 ? 'done' : ''}`}>
              <span>{step === 3 ? <FaCheck /> : '3'}</span><em>완료</em>
            </div>
          </div>

          {/* STEP 1 */}
          {step < 3 && (
            <form onSubmit={handleSend}>
              <div className='field-block'>
                <label>*아이디</label>
                <div className='field-row'>
                  <input
                    type='text'
                    value={userId}
                    onChange={(e) => { setUserId(e.target.value); setCodeSent(false); clearInterval(timerRef.current); setTimeLeft(0); }}
                    placeholder='가입한 아이디'
                    disabled={codeSent && timeLeft > 0}
                  />
                </div>
              </div>
              <div className='field-block'>
                <label>*휴대폰 번호</label>
                <div className='field-row'>
                  <input
                    type='tel'
                    value={hp}
                    onChange={(e) => { setHp(e.target.value); setCodeSent(false); clearInterval(timerRef.current); setTimeLeft(0); }}
                    placeholder='01012345678'
                    maxLength={11}
                    disabled={codeSent && timeLeft > 0}
                  />
                  <button
                    type='submit'
                    className='btn-send'
                    disabled={sending || (codeSent && timeLeft > 0)}
                  >
                    {sending ? '전송중...' : codeSent ? '재전송' : '인증번호 전송'}
                  </button>
                </div>
                {codeSent && timeLeft > 0 && <p className='sent-notice'>인증번호가 발송되었습니다.</p>}
              </div>
            </form>
          )}

          {/* STEP 2 */}
          {codeSent && step < 3 && (
            <form onSubmit={handleReset}>
              <div className='field-block'>
                <label>*인증번호</label>
                <div className='field-row'>
                  <div className='code-wrap'>
                    <input
                      type='text'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder='6자리 입력'
                      maxLength={6}
                    />
                    <span className={`timer-badge ${timeLeft <= 60 ? 'urgent' : ''}`}>
                      {timeLeft > 0 ? formatTime(timeLeft) : '만료'}
                    </span>
                  </div>
                </div>
              </div>
              <div className='field-block'>
                <label>*새 비밀번호</label>
                <div className='field-row'>
                  <input
                    type='password'
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder='새 비밀번호 (6자 이상)'
                  />
                </div>
              </div>
              <div className='field-block'>
                <label>*비밀번호 확인</label>
                <div className='field-row'>
                  <input
                    type='password'
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder='비밀번호 재입력'
                  />
                </div>
              </div>
              <div className='modal-footer' style={{ padding: 0, border: 'none' }}>
                <button type='submit' className='btn-primary'>비밀번호 변경</button>
              </div>
            </form>
          )}

          {/* STEP 3 - 완료 */}
          {step === 3 && (
            <div className='result-panel'>
              <div className='result-icon'><FaCheck /></div>
              <p className='result-label'>비밀번호가 변경되었습니다.</p>
              <p className='result-label' style={{ marginTop: '0.5rem' }}>다시 로그인해주세요.</p>
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn-secondary' onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FindPasswordModal;
